<script setup lang="ts">
import { ref, computed, onUnmounted } from 'vue'

import { ProfileSteps } from '@/constant/user'
import appStore from '@/stores'

import UpdateEmailDone from './email/UpdateEmailDone.vue'
import UpdateEmailStep1 from './email/UpdateEmailStep1.vue'
import UpdateEmailStep2 from './email/UpdateEmailStep2.vue'
import UpdatePassword from './password/UpdatePassword.vue'
import UpdatePhone from './phone/UpdatePhone.vue'
import ProfileInfo from './ProfileInfo.vue'
import BindingAccount from './sso/BindingAccount.vue'

const PROFILE_PANEL_OPTIONS = {
  ACCOUNT: 'account',
  BINDING: 'binding'
} as const

export type ProfilePanelOption = (typeof PROFILE_PANEL_OPTIONS)[keyof typeof PROFILE_PANEL_OPTIONS]

interface ProfilePanelMenuItem {
  title: string
  id: ProfilePanelOption
}

const feUserProfileStore = appStore.useFEUserProfileStore
const authStore = appStore.useAuthStore

const currentStepComponent = computed(() => {
  switch (feUserProfileStore.currentStep) {
    case ProfileSteps.UPDATE_EMAIL_STEP_1:
      return UpdateEmailStep1
    case ProfileSteps.UPDATE_EMAIL_STEP_2:
      return UpdateEmailStep2
    case ProfileSteps.UPDATE_EMAIL_DONE:
      return UpdateEmailDone
    case ProfileSteps.UPDATE_PASSWORD:
      return UpdatePassword
    case ProfileSteps.UPDATE_PHONE:
      return UpdatePhone
    case ProfileSteps.BINDING_ACCOUNT:
      return BindingAccount
    default:
      return ProfileInfo
  }
})

const profilePanelMenu = computed(() => {
  const menu: ProfilePanelMenuItem[] = [{ title: '帳戶', id: PROFILE_PANEL_OPTIONS.ACCOUNT }]
  if (authStore.isNativeAccount) {
    menu.push({ title: '第三方帳號管理', id: PROFILE_PANEL_OPTIONS.BINDING })
  }
  return menu
})

const selectedOption = ref<ProfilePanelOption>(PROFILE_PANEL_OPTIONS.ACCOUNT)
const setSelectedOption = (option: ProfilePanelOption) => {
  selectedOption.value = option
  if (option === PROFILE_PANEL_OPTIONS.BINDING) {
    feUserProfileStore.updateCurrentStep(ProfileSteps.BINDING_ACCOUNT)
  } else {
    feUserProfileStore.updateCurrentStep(ProfileSteps.PROFILE_INFO)
  }
}

onUnmounted(() => {
  feUserProfileStore.updateCurrentStep(ProfileSteps.PROFILE_INFO)
  selectedOption.value = PROFILE_PANEL_OPTIONS.ACCOUNT
})
</script>

<template>
  <div class="profile-dialog-content" :class="authStore.isNativeAccount ? 'h-[600px]' : 'h-[540px]'">
    <aside class="profile-select-list-wrapper">
      <ul class="profile-select-list flex cursor-pointer flex-col gap-2.5">
        <li
          v-for="item in profilePanelMenu"
          :key="item.id"
          class="rounded-[4px] py-1.5 pl-5 text-sm font-medium"
          :class="selectedOption === item.id ? 'bg-app-light-blue-50/30 text-app-brand-blue-500' : 'text-app-black-500'"
          @click="setSelectedOption(item.id)"
        >
          {{ item.title }}
        </li>
      </ul>
    </aside>
    <div class="profile-content">
      <component :is="currentStepComponent" />
    </div>
  </div>
</template>

<style lang="scss" scoped>
.profile-dialog-content {
  border-top: 1px solid var(--primary-5);
  display: flex;
  margin-bottom: -24px;
}

.profile-select-list {
  margin-top: 13px;
  &-wrapper {
    padding-left: 18px;
    padding-right: 18px;
    border-right: 1px solid var(--primary-5);
    width: 24%;
    font-weight: 500;
  }
  &-item {
    cursor: pointer;
    padding: 6px 19px;
    border-radius: 4px;
    font-weight: 500;
    letter-spacing: 1.5px;
  }
  &-item.active {
    background-color: #f3f6f9;
    color: var(--primary-3);
  }
}

.profile-content {
  width: 76%;
  padding-left: 28px;
  padding-right: 38px;
  padding-bottom: 32px;
}
</style>
